/* eslint-disable react/jsx-props-no-spreading */
import { useMemo } from 'react';
import { JobCardBaseProps, NavigateHandler } from '../../index';
import {
  CardLayout as Card,
  Header,
  Body,
  Footer,
  getHeaderProps,
  getFooterProps,
  type GetPropsParam,
} from '../Layout';
import { InfoList } from '../InfoList';
import { AppealA } from '../Appeal';
import styles from './index.module.scss';

/**
 * Job Card A コンポーネントのプロパティ
 * @interface JobCardAProps
 * @extends {JobCardBaseProps}
 */
interface JobCardAProps extends JobCardBaseProps {}

/**
 * Job Card A コンポーネント
 * @param {JobCardAProps} props - Job Card A コンポーネントのプロパティ
 * @returns {JSX.Element} レンダリングされたコンポーネント
 */
export const JobCardA = ({
  job,
  destinationURL,
  bookmarkUrlQuery,
  similarKeywordArea,
  ...handlers
}: JobCardAProps) => {
  const {
    isApplicationWelcome,
    isApplied,
    isBookmarkSaved,
    jobContentOutline,
    targetMemberOutline,
    jobState,
    stations,
    salary,
    salaryExamples,
    businessContent,
    listExclusiveHeading,
    listExclusiveSubHeading,
    listExclusiveMessage,
  } = job;

  const {
    onNavigateTitle,
    onNavigateFooterButton,
    onApplyDirect,
    onApplyAgent,
    onBookmark,
    onNavigateToBookmarkList,
  } = handlers;

  const [headerProps, footerProps] = useMemo(() => {
    const arg: GetPropsParam = { job };
    return [getHeaderProps(arg), getFooterProps(arg)];
  }, [job]);

  /**
   * 求人詳細画面へのナビゲーションハンドラー
   * @param {NavigateHandler} [handler] - オプションのイベントハンドラー
   * @returns {NavigateHandler} ナビゲーション用のイベントハンドラー
   */
  const handleNavigateToJobDetail =
    (handler?: NavigateHandler): NavigateHandler =>
    (event) => {
      event.currentTarget.href = destinationURL;
      if (handler) handler(event);
    };

  return (
    <Card
      isWelcome={isApplicationWelcome}
      isApplied={isApplied}
      isBookmarked={isBookmarkSaved}
    >
      <Header
        {...headerProps}
        onNavigateTitle={handleNavigateToJobDetail(onNavigateTitle)}
        onApplyDirect={onApplyDirect}
        onApplyAgent={onApplyAgent}
        similarKeywordArea={similarKeywordArea}
      />
      <Body>
        <div className={styles['jobCardA-body']}>
          <InfoList
            detail={jobContentOutline}
            target={targetMemberOutline}
            location={jobState}
            station={stations}
            salary={{
              salary,
              example: salaryExamples,
            }}
            business={businessContent}
            isCardTypeA
          />
          <div className={styles['jobCardA-body__right']}>
            <AppealA
              title={listExclusiveHeading}
              subTitle={listExclusiveSubHeading}
              body={listExclusiveMessage}
            />
          </div>
        </div>
      </Body>
      <Footer
        {...footerProps}
        bookmarkUrlQuery={bookmarkUrlQuery}
        onNavigateFooterButton={handleNavigateToJobDetail(
          onNavigateFooterButton
        )}
        onBookmark={onBookmark}
        onNavigateToBookmarkList={onNavigateToBookmarkList}
      />
    </Card>
  );
};
